<script setup lang="ts">
import { ref, watch } from "vue";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Select from "primevue/select";
import Button from "primevue/button";
import type { FetchRecipeResponse } from "src/api/models/fetchRecipeResponse";
import type { UpdateActionDTO } from "src/api/models/updateActionDTO";
import { CreateRecipeDTOType } from "src/api/models/createRecipeDTOType";

const props = defineProps<{
  recipe: FetchRecipeResponse | null;
  onSubmit: (id: string, data: UpdateActionDTO) => Promise<void>;
}>();

const visible = defineModel<boolean>("visible", { default: false });

const typeOptions = Object.values(CreateRecipeDTOType).map((value) => ({
  label: value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  value,
}));

const form = ref({ name: "", price: 0, type: "" });
const submitting = ref(false);
const error = ref<string | null>(null);

watch(visible, (isVisible) => {
  if (isVisible && props.recipe) {
    form.value = {
      name: props.recipe.name ?? "",
      price: props.recipe.price ?? 0,
      type: props.recipe.type ?? "",
    };
    error.value = null;
  }
});

async function handleSubmit() {
  if (!props.recipe?.id) return;
  if (!form.value.name.trim() || !form.value.type) {
    error.value = "Name and type are required";
    return;
  }
  submitting.value = true;
  error.value = null;
  try {
    await props.onSubmit(props.recipe.id, {
      name: form.value.name.trim(),
      price: form.value.price,
      type: form.value.type as UpdateActionDTO["type"],
    });
    visible.value = false;
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : "Failed to update recipe";
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    header="Edit Recipe"
    :modal="true"
    :style="{ width: '480px' }"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="font-semibold text-sm">Name</label>
        <InputText v-model="form.name" placeholder="Recipe name" :disabled="submitting" />
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-semibold text-sm">Price</label>
        <InputNumber
          v-model="form.price"
          mode="currency"
          currency="EUR"
          locale="nl-NL"
          :min="0"
          :disabled="submitting"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-semibold text-sm">Type</label>
        <Select
          v-model="form.type"
          :options="typeOptions"
          option-label="label"
          option-value="value"
          placeholder="Select a type"
          :disabled="submitting"
        />
      </div>

      <small v-if="error" class="text-red-500">{{ error }}</small>
    </div>

    <template #footer>
      <Button label="Cancel" severity="secondary" text :disabled="submitting" @click="visible = false" />
      <Button
        label="Save"
        icon="pi pi-check"
        :loading="submitting"
        @click="handleSubmit"
      />
    </template>
  </Dialog>
</template>
